import { useState } from 'react';
import { DataGridPro, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { Box, Typography, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { formatCurrency } from 'src/utils/format-currency';
import { useLocales } from 'src/locales';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import CustomLineChart from '../inline-chart/inline-chart';
import Iconify from '../iconify';
import countries from '../../utils/countries.json';
import { formatNumber } from 'src/utils/format-number';
import { useTheme } from '@mui/material/styles';
import { useResponsive } from 'src/hooks/use-responsive';
import CustomTooltip from '../competitive-intelligence/components/CustomTooltip';
import { levelSortComparator, normalizeLevel, PresenceSegments } from '../competitive-intelligence';
import CompetitiveLandscapeCell from '../competitive-intelligence/components/CompetitiveLandscapeCell';
import Chart, { useChart } from '../chart';
import { useRouter } from 'src/routes/hooks';
import { useAuth } from 'src/auth';

const getCountryCode = (name: any) =>
    countries.find((c) => c.name.toLowerCase() === name.toLowerCase())?.code;


const PresenceRateChart = ({ data, height = 40 }: { data: PresenceSegments, height?: number }) => {
    const theme = useTheme();

    const calculateCompetitorsOnly = (data: PresenceSegments) => {
        return 100 - data.organicOnly - data.soloBidder - data.topAdPosition;
    };

    const chartOptions = useChart({
        chart: {
            type: 'bar',
            stacked: true,
            sparkline: {
                enabled: true,
            },
            toolbar: {
                show: false,
            },
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '30%',
                borderRadius: 0,
            },
        },
        stroke: {
            width: 0,
            show: false,
        },
        series: [
            { name: 'Organic only', data: [data.organicOnly] },
            { name: 'Solo bidder', data: [data.soloBidder] },
            { name: 'Top ad position', data: [data.topAdPosition] },
            { name: 'Competitors only', data: [calculateCompetitorsOnly(data)] },
        ],
        colors: ['#18A155', '#C09910', '#F9DE81', 'rgba(241, 255, 170, 0.10)'],
        tooltip: {
            enabled: false,
            x: { show: false },
            y: {
                formatter: (val: number) => `${Math.round(val)}%`,
            },
            marker: { show: true },
        },
        legend: {
            show: false,
        },
        grid: {
            show: false,
        },
        xaxis: {
            labels: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            min: 0,
            max: 100,
        },
        yaxis: {
            labels: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
        },
    });

    return (
        <Chart
            type="bar"
            series={chartOptions.series}
            options={chartOptions}
            height={height}
        />
    );
};

const getColumns = ({ t, workspace, currency, locale, lgUp, xlUp }: any) => {
    return [
        {
            field: 'split',
            headerName: 'Country',
            renderHeader: () => (
                <div style={{ textAlign: 'right' }}>{t("dashboard.global.country")}</div>
            ),
            flex: 1,
            minWidth: 160,

            renderCell: ({ value }: any) => {
                const code = getCountryCode(value);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {code ? (
                            <img
                                src={`https://flagcdn.com/w20/${code}.png`}
                                alt={value}
                                style={{ width: 20, height: 15, borderRadius: 2 }}
                            />
                        ) : (
                            <img
                                style={{ width: 14, height: 14, marginRight: 6 }}
                                src="/assets/location.png"
                                alt="flag"
                            />
                        )}
                        {value}
                    </Box>
                );
            },
        },
        // === exemple avec une métrique (savings) ===
        {
            field: 'savings',
            headerName: t('dashboard.filters.savings'),
            minWidth: 125,
            flex: 1,
            renderHeader: () => (
                <div style={{ textAlign: 'right' }}>{t('dashboard.filters.savings')}</div>
            ),
            renderCell: (params: any) => {
                const series = [
                    {
                        name: 'Savings',
                        data:
                            params.row.sparkline_savings?.map((p: any) => ({
                                x: new Date(p.date).getTime(),
                                y: p.value,
                            })) || [],
                    },
                ];
                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ px: 1, width: 1, height: 1 }}
                    >
                        <CustomLineChart
                            series={series}
                            formatValue={(v) => formatCurrency(v, currency, locale, true, 2)}
                            color={'#36B37E'}
                            type="bar"
                            height={30}
                            width={100}
                        />
                        <Typography variant="caption" sx={{ width: 80, textAlign: 'right' }}>
                            {formatCurrency(params.value, currency, locale, true, 1)}
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        {
            field: 'brand_traffic',
            headerName: t('dashboard.brandTraffic.totalBrandTraffic'),
            minWidth: 200,
            flex: 1,
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>{t('dashboard.brandTraffic.totalBrandTraffic')}</span>
                </div>
            ),
            renderCell: (params: any) => {
                const series = [{
                    name: t('dashboard.brandTraffic.totalBrandTraffic'),
                    data: params.row.sparkline_brand_traffic?.map((point: any) => ({
                        x: new Date(point.date).getTime(),
                        y: point.value
                    })) || []
                }];
                return (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, width: 1, height: 1 }}>
                        <CustomLineChart series={series} formatValue={(value) => formatNumber(value)} color={'#880e4f'} type="line" height={30} width={100} />

                        <Typography variant="caption" sx={{ width: 80, textAlign: "right" }}>
                            {formatNumber(params.value)}
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        {
            field: 'blended_ctr',
            headerName: t('dashboard.brandTraffic.blendedCtr'),
            minWidth: 200,
            flex: 1,
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>{t('dashboard.brandTraffic.blendedCtr')}</span>
                </div>
            ),
            renderCell: (params: any) => {
                const value = params.value ?? 0;
                const series = [{
                    name: t('dashboard.brandTraffic.blendedCtr'),
                    data: params.row.sparkline_blended_ctr?.map((point: any) => ({
                        x: new Date(point.date).getTime(),
                        y: point.value
                    })) || []
                }];
                return (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, width: 1, height: 1 }}>
                        <CustomLineChart color={'#5119b7'} series={series} formatValue={(val) => `${(val).toFixed(1)}%`} type="line" height={30} width={100} />

                        <Typography variant="caption" sx={{ width: 80, textAlign: "right" }}>
                            {((value).toFixed(1))}%
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        {
            field: 'ad_cost_with_cb',
            minWidth: 170,
            flex: 1,
            renderHeader: () => (
                <div style={{ textAlign: 'right' }}>Ad Cost with CB</div>
            ),
            renderCell: (params: any) => {
                const series = [{
                    name: 'Ad Cost w/ CB',
                    data: params.row.sparkline_ad_cost_with_cb?.map((point: any) => ({
                        x: new Date(point.date).getTime(),
                        y: point.value
                    })) || []
                }];
                return (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, width: 1, height: 1 }}>

                        <CustomLineChart series={series} formatValue={(value) => formatCurrency(value, currency, locale, true, 2)} color={'#ffab00'} type="bar" height={30} width={100} />

                        <Typography variant="caption" sx={{ width: 80, textAlign: "right" }}>
                            {formatCurrency(params.value, currency, locale, true, 1)}
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        {
            field: 'revenue_or_conversions',
            headerName: workspace?.template === 'Lead'
                ? t('dashboard.brandOverview.conversions')
                : t('dashboard.revenue'),
            minWidth: 180,
            flex: 1,
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>
                        {workspace?.template === 'Lead'
                            ? t('dashboard.brandOverview.conversions')
                            : t('dashboard.revenue')}
                    </span>
                </div>
            ),
            renderCell: (params: any) => {
                const value = params.value ?? 0;
                const series = [{
                    name: workspace?.template === 'Lead'
                        ? t('dashboard.brandOverview.conversions')
                        : t('dashboard.revenue'),
                    data: params.row.sparkline_revenue_or_conversions?.map((point: any) => ({
                        x: new Date(point.date).getTime(),
                        y: point.value
                    })) || []
                }];
                return (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, width: 1, height: 1 }}>
                        <CustomLineChart series={series} formatValue={(val) => workspace?.template === 'Lead' ? formatNumber(val) : formatCurrency(val, currency, locale, true, 2)} color={'#2A6FD6'} type="bar" height={30} width={100} />

                        <Typography variant="caption" sx={{ width: 80, textAlign: "right" }}>
                            {workspace?.template === 'Lead'
                                ? formatNumber(value)
                                : formatCurrency(value, currency, locale, true, 1)}
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        {
            field: 'competitiveLandscape',
            headerName: t('dashboard.competitiveIntelligence.competitivePressure'),
            width: 200,
            sortable: true,
            align: 'left',
            headerAlign: 'right',
            valueGetter: (_value: any, row: any) => {
                const raw = (row?.levelRaw as string) ?? '';
                return normalizeLevel(raw);
            },


            sortComparator: levelSortComparator,

            renderHeader: (params: any) => (
                <CustomTooltip title={t('dashboard.global.kpiDefinitions.competitivePressure') ?? ''} placement="top">
                    <span style={{ fontSize: lgUp ? 14 : 12 }}>{params.colDef.headerName}</span>
                </CustomTooltip>
            ),
            renderCell: (params: any) => {
                if (params.row?.status === 'not scrapped') return null;
                return (
                    <>
                        {params?.row?.firstCompetitorDomain &&
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <CompetitiveLandscapeCell
                                    level={params.row.levelRaw}
                                    brandDomain={params.row.brandDomain}
                                    competitorDomain={params.row.firstCompetitorDomain}
                                    brandPresenceRate={params.row.paidPresenceRate}
                                    competitorPresenceRate={params.row.firstCompetitorPaidPresenceRate}
                                    isDemoBrand={params.row.isDemoBrand}
                                />
                            </Box>
                        }
                    </>
                );
            },

        },
        {
            field: 'blendedPresenceRate',
            headerName: t('dashboard.competitiveIntelligence.blendedPresenceRate'),
            // Partager l'espace restant avec topCompetitors
            flex: 1,
            minWidth: lgUp ? (xlUp ? 260 : 250) : 280,
            renderHeader: (params: any) => (
                <CustomTooltip title={t('dashboard.global.kpiDefinitions.blendedPresenceRate') ?? ''} placement="top">
                    <span style={{ fontSize: lgUp ? 14 : 12 }}>{params.colDef.headerName}</span>
                </CustomTooltip>
            ),
            headerAlign: 'right',
            renderCell: (params: any) => {
                if (params.row?.status === 'not scrapped') return null;

                return (
                    <>
                        {params.value &&
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    width: '100%',
                                    height: '100%', // ✅ occupe toute la hauteur de la cellule
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 'medium', minWidth: lgUp ? 40 : 30, fontSize: lgUp ? 14 : 12 }}>
                                    {Math.round(params.value)}%
                                </Typography>
                                <CustomTooltip
                                    placement="top"
                                    title={
                                        <Stack direction="column" alignItems="flex-start" spacing={0.5} padding={1}>

                                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                <Iconify icon="solar:global-linear" sx={{ width: 16, height: 16, color: '#18A155' }} />
                                                <Typography variant="caption">Organic only: {Math.round(params.row.presenceRateData.organicOnly)}%</Typography>
                                            </Stack>
                                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                <Iconify icon="carbon:user" sx={{ width: 16, height: 16, color: '#C09910' }} />
                                                <Typography variant="caption">Solo bidder: {Math.round(params.row.presenceRateData.soloBidder)}%</Typography>
                                            </Stack>
                                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                <Iconify icon="carbon:user-multiple" sx={{ width: 16, height: 16, color: '#F9DE81' }} />
                                                <Typography variant="caption">Top ad position: {Math.round(params.row.presenceRateData.topAdPosition)}%</Typography>
                                            </Stack>
                                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                                                <Iconify icon="ri:sword-line" sx={{ width: 16, height: 16, color: 'rgba(241, 255, 170, 0.10)' }} />
                                                <Typography variant="caption">Competitors only: {Math.round(100 - params.row.blendedPresenceRate)}%</Typography>
                                            </Stack>
                                        </Stack>
                                    }
                                >
                                    <Box>
                                        <PresenceRateChart data={params.row.presenceRateData} />
                                    </Box>
                                </CustomTooltip>
                            </Box>
                        }
                    </>
                );
            },
        },
    ];
};


export default function DataGridPerformanceTableBrandOverview({ data, compare }: any) {
    const filterStore = useSelector((state: any) => state.filters);
    const { t, currentLang } = useLocales();
    const locale = currentLang.value;
    const workspace = useCurrentWorkspace();
    const currency = workspace?.currency || 'USD';
    const theme = useTheme();
    const filteredRows = data.filter((row: any) => row.country !== null);
    const isCompare = !!filterStore?.compare;
    const lgUp = useResponsive('up', 'lg');
    const xlUp = useResponsive('up', 'xl');
    const router = useRouter();
    const { user } = useAuth();
    const role = user?.role;

    const columns: any = getColumns({
        t,
        workspace,
        theme,
        isCompare,
        currency,
        locale,
        lgUp,
        xlUp
    });

    const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });

    const [columnVisibilityModel, setColumnVisibilityModel] =
        useState<GridColumnVisibilityModel>({
            savings_diff: false,
            ad_cost_without_cb: false,
            ad_cost_with_cb_diff: false,
            ad_cost_without_cb_diff: false,
            revenue_or_conversions_diff: true,
            blended_ctr_diff: true,
            topCompetitors: false,
        });

    return (
        <Box
            sx={{
                position: 'relative',
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                    outline: 'none !important',
                },
                '& .MuiDataGrid-cell--pinnedLeft': {
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? '#212b36 !important'
                            : 'white !important',

                },
                '& .MuiDataGrid-cell': {
                    overflow: 'visible !important',
                    borderRight: '1px solid #E0E0E0',
                    borderBottom: '1px solid #E0E0E0',
                },
                '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer': {
                    display: 'flex',
                    justifyContent: 'flex-end',
                    backgroundColor: 'white!important'

                },
                "& .MuiDataGrid-columnHeader": {
                    borderRight: '1px solid #E0E0E0',
                    borderBottom: '1px solid #E0E0E0',
                }
            }}
        >
            <DataGridPro
                autoHeight
                disableRowSelectionOnClick
                sx={{ borderRadius: 1 }}
                rows={filteredRows.map((r: any, i: any) => ({ id: i, ...r }))}
                pagination
                columnVisibilityModel={columnVisibilityModel}

                onColumnVisibilityModelChange={setColumnVisibilityModel}
                paginationModel={paginationModel}
                columns={columns}
                onPaginationModelChange={setPaginationModel}
                disableDensitySelector
                columnGroupingModel={[
                    {
                        groupId: 'Group1',
                        renderHeaderGroup: () => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                                onClick={() => router.push('/optimization/overview')}
                            >
                                <Typography color={"rgba(30, 136, 229, 1)"} variant="subtitle2">Optimization Hub</Typography>
                                <Iconify color={"rgba(30, 136, 229, 1)"} icon="eva:diagonal-arrow-right-up-fill" width={18} height={18} />
                            </Box>
                        ),
                        children: [
                            { field: 'savings' },
                            { field: 'brand_traffic' },
                            { field: 'blended_ctr' },
                            { field: 'ad_cost_with_cb' },
                            { field: 'revenue_or_conversions' },
                        ],
                    },
                    {
                        groupId: 'Group2',
                        renderHeaderGroup: () => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                                onClick={() => router.push('/competitors-monitoring')}
                            >
                                <Typography color={"rgba(30, 136, 229, 1)"} variant="subtitle2">Competitive intelligence</Typography>
                                <Iconify color={"rgba(30, 136, 229, 1)"} icon="eva:diagonal-arrow-right-up-fill" width={18} height={18} />
                            </Box>
                        ), children: [
                            { field: 'competitiveLandscape' },
                            { field: "topCompetitors" },
                            { field: 'blendedPresenceRate' }
                        ],
                    },
                ]}
            />
        </Box>
    );
}